import { esc } from "./stars";

interface DateChipSelectorOptions {
  fieldName: "dateStarted" | "dateRead";
  fieldId: string;
  label: string;
  currentValue?: string;
}

type ChipState = "none" | "today" | "pick";

function getToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function formatDate(isoString: string): string {
  const parts = isoString.split("T")[0].split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  const date = new Date(year, month, day);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function deriveChipState(currentValue?: string): ChipState {
  if (!currentValue) return "none";
  if (currentValue === getToday()) return "today";
  return "pick";
}

export function renderDateChipSelector(options: DateChipSelectorOptions): string {
  const { fieldName, fieldId, label, currentValue } = options;
  const state = deriveChipState(currentValue);
  const value = currentValue ?? "";

  const noneActive = state === "none" ? " date-chip--active" : "";
  const todayActive = state === "today" ? " date-chip--active" : "";
  const pickActive = state === "pick" ? " date-chip--active" : "";

  const todayLabel =
    state === "today" ? `Today · ${formatDate(currentValue!)}` : "Today";
  const pickLabel =
    state === "pick" ? formatDate(currentValue!) : "Pick\u2026";

  return `
      <div class="book-form__field">
        <label class="book-form__label">${esc(label)}</label>
        <div class="date-chips" data-field="${fieldName}">
          <button type="button" class="date-chip date-chip--tan${noneActive}">None</button>
          <button type="button" class="date-chip date-chip--blue${todayActive}">${todayLabel}</button>
          <button type="button" class="date-chip date-chip--gold${pickActive}">${pickLabel}</button>
          <input type="hidden" name="${fieldName}" id="${fieldId}" value="${esc(value)}" />
          <div class="date-chips__calendar-anchor"></div>
        </div>
      </div>`;
}

export function bindDateChipEvents(
  container: HTMLElement,
  onCalendarRequest: (fieldName: string, currentValue: string) => void,
): void {
  container.querySelectorAll<HTMLElement>(".date-chips").forEach((chips) => {
    const fieldName = chips.dataset.field!;
    const noneBtn = chips.querySelector<HTMLButtonElement>(".date-chip--tan")!;
    const todayBtn = chips.querySelector<HTMLButtonElement>(".date-chip--blue")!;
    const pickBtn = chips.querySelector<HTMLButtonElement>(".date-chip--gold")!;
    const hiddenInput = chips.querySelector<HTMLInputElement>("input[type=hidden]")!;

    function setActive(active: HTMLButtonElement): void {
      noneBtn.classList.remove("date-chip--active");
      todayBtn.classList.remove("date-chip--active");
      pickBtn.classList.remove("date-chip--active");
      active.classList.add("date-chip--active");
    }

    noneBtn.addEventListener("click", () => {
      hiddenInput.value = "";
      setActive(noneBtn);
      todayBtn.textContent = "Today";
      pickBtn.textContent = "Pick\u2026";
    });

    todayBtn.addEventListener("click", () => {
      const today = getToday();
      hiddenInput.value = today;
      setActive(todayBtn);
      todayBtn.textContent = `Today \u00b7 ${formatDate(today)}`;
      pickBtn.textContent = "Pick\u2026";
    });

    pickBtn.addEventListener("click", () => {
      onCalendarRequest(fieldName, hiddenInput.value);
    });
  });
}

export function updateChipToPickState(
  container: HTMLElement,
  dateValue: string,
): void {
  const hiddenInput = container.querySelector<HTMLInputElement>("input[type=hidden]")!;
  const noneBtn = container.querySelector<HTMLButtonElement>(".date-chip--tan")!;
  const todayBtn = container.querySelector<HTMLButtonElement>(".date-chip--blue")!;
  const pickBtn = container.querySelector<HTMLButtonElement>(".date-chip--gold")!;

  hiddenInput.value = dateValue;
  noneBtn.classList.remove("date-chip--active");
  todayBtn.classList.remove("date-chip--active");
  pickBtn.classList.remove("date-chip--active");
  pickBtn.classList.add("date-chip--active");
  pickBtn.textContent = formatDate(dateValue);
  todayBtn.textContent = "Today";
}
